import { INSTANCE } from '../instance';

/**
 * Gate resolvers for navigation. TWO independent gates live here, and both must
 * pass for something to be visible:
 *   - `userCanAccess`  — may this PERSON see it (capabilities/RBAC)
 *   - `instanceAllows` — does this DEPLOYMENT have it at all
 * Consumed by navTree.ts, useNavAlerts, Bookmarks and useUserStore.
 *
 * DEPRECATED — the mega-menu (navTree.ts) superseded the flat config below.
 * `NAV_ITEMS`, `getGroupItems`, and `getMobileMenuItems` have NO importers and
 * are pending removal. Do not add consumers; the nav structure lives in navTree.ts.
 */

export const NAV_ITEMS = [
  // Main navigation
  { to: '/fleet',            label: 'Fleet',          group: 'main' },
  { to: '/assets',           label: 'Assets',         group: 'main', require: 'assetsRead' },
  // Inventory promoted to main nav next to Assets — the daily warehouse
  // surface for "what stock do I have." Catalog stays in More since it's an
  // admin-y surface (SKU metadata editing, not stock browsing).
  { to: '/inventory',        label: 'Inventory',      group: 'main', require: 'catalogRead' },
  { to: '/receiving',        label: 'Receiving',      group: 'main', require: 'receivingHub' },
  // Receipts — every authenticated user scans/reviews their own (no gate).
  { to: '/receipts',         label: 'Receipts',       group: 'main' },
  // Phase 1b: legacy /parts nav item removed in the 1b.08 cutover.
  { to: '/parts-catalog',    label: 'Catalog',        group: 'more', require: 'catalogRead' },
  // Financial-only surface — cost/price audit (transactions + price changes).
  // Strict gate (admins must hold can_financial too); see userCanAccess('financial').
  { to: '/financial-logs', label: 'Financial Logs', group: 'more', require: 'financial' },
  { to: '/calendar',          label: 'Calendar',       group: 'main', require: 'hasRole' },
  // Events dropdown
  { to: '/events',           label: 'Events',         group: 'events', require: 'hasRole' },
  { to: '/schedule',         label: 'Schedule',       group: 'events', require: 'producer', indent: true },
  { to: '/roster',           label: 'Roster',         group: 'events', require: 'producer', indent: true },
  { to: '/tour-timeline',    label: 'Tour Timeline',  group: 'events', require: 'hasRole', indent: true },
  // Pilots (own group so desktop NavBar can place it between Events and Dispatch)
  { to: '/pilots',           label: 'Pilots',         group: 'pilot', require: 'poa' },
  // Dispatch dropdown (also serves the vehicle fleet; will serve work orders)
  { to: '/dispatch',         label: 'Dispatch',       group: 'dispatch', require: 'hasRole' },
  { to: '/vehicles',         label: 'Vehicles',       group: 'dispatch', require: 'hasRole', indent: true },

  // Customers dropdown
  { to: '/customers',        label: 'Customers',      group: 'customers', require: 'sales' },
  { to: '/orders',           label: 'Orders',         group: 'customers', require: 'hasRole', indent: true },
  { to: '/customers/transfers', label: 'Drone Transfers', group: 'customers', require: 'sales', indent: true },
  // Analysis
  { to: '/analyst',          label: 'Analyst',        group: 'main', require: 'analyst', instances: ['firefly', 'swarm'] },

  // More dropdown
  { to: '/charge',           label: 'Charging',       group: 'more', require: 'chargeControl' },
  { to: '/firmware',         label: 'Firmware',       group: 'more', require: 'firmware' },
  { to: '/scanner',          label: 'Scanner Mode',   group: 'more' },
  { to: '/map',              label: 'Map',            group: 'more' },
  { to: '/maintenance',      label: 'Logs',           group: 'more' },
  { to: '/leaderboards',     label: 'Leaderboards',   group: 'more' },
  { to: '/statistics',       label: 'Statistics',     group: 'more' },

  // RMA section
  { to: '/rma',              label: 'RMA List',       group: 'rma' },
  { to: '/rma/submit',       label: 'Public Submit',  group: 'rma', indent: true },
  { to: '/rma/status',       label: 'Public Status',  group: 'rma', indent: true },

  // User actions
  { to: '/my-schedule',      label: 'My Schedule',    group: 'user', require: 'canTravel' },
  { action: 'customize',     label: 'Customize Dashboard', group: 'user' },
  { to: '/settings',         label: 'Settings',       group: 'user' },
  { to: '/admin',            label: 'Admin',          group: 'user', require: 'admin', divider: true },

  // External
  { to: 'https://app.gitbook.com/o/0FMfwLrztiEc1D725YNJ/s/sCDLcJrn8yHLadQxxMGQ/', label: 'Gitbook', group: 'external', external: true },
];

/**
 * Does this deployment have the feature at all?
 *
 * ORTHOGONAL to userCanAccess, and deliberately a separate function: they answer
 * different questions. userCanAccess asks "may this person?", instanceAllows asks
 * "does this product have this?". Folding one into the other produces bugs where
 * granting a capability reveals a feature the product does not have.
 *
 * **Absent `instances` means Firefleet-only.** Allowlist, never denylist — a
 * denylist silently regains every feature Firefleet ships next. Opt a feature in
 * to SwarmDefense.Systems by giving it `instances: BOTH` in navTree.ts.
 *
 * See docs/operations/two-instances.md.
 */
/**
 * The allowlist default lives HERE and nowhere else. Parameterised on the
 * deployment so routeInstances.ts can answer for the other one (and so its
 * tests can, without a rebuild).
 */
export const allowsInstance = (item, inst) =>
  (item?.instances || ['firefly']).includes(inst);

export const instanceAllows = (item) => allowsInstance(item, INSTANCE);

/**
 * Check if a user meets an item's access requirement.
 */
export const userCanAccess = (item, user) => {
  if (!item.require) return true;

  switch (item.require) {
    case 'hasRole':
      return !!(user?.isadmin || user?.can_admin || user?.can_operations ||
                user?.can_manufacturing || user?.can_producer || user?.can_sales ||
                user?.can_pilot || user?.can_manager || user?.can_procurement);
    case 'sales':
      return !!(user?.isadmin || user?.can_sales);
    // Mirrors requireCapability('admin', 'operations'), e.g. the scanner's Assign commit.
    case 'operations':
      return !!(user?.isadmin || user?.can_admin || user?.can_operations);
    case 'admin':
      return !!(user?.isadmin || user?.can_admin);
    case 'manager':
      return !!(user?.isadmin || user?.can_admin || user?.can_manager);
    case 'analyst':
      return !!(user?.isadmin || user?.can_admin || user?.can_analyst);
    case 'producer':
      return !!(user?.isadmin || user?.can_admin || user?.can_producer);
    case 'pilot':
      return !!(user?.isadmin || user?.can_admin || user?.can_pilot);
    case 'manufacturing':
      // Exactly mirrors the backend requireCapability('manufacturing'), which
      // bypasses on isadmin/can_admin. Nav and API have to agree here or the
      // link is visible and then 403s, which reads as a broken page.
      return !!(user?.isadmin || user?.can_admin || user?.can_manufacturing);
    // Mirrors requireCapability('rnd'): admin bypass plus the can_rnd flag.
    case 'rnd':
      return !!(user?.isadmin || user?.can_admin || user?.can_rnd);
    // Mirrors api/src/spark/spark-access.js canRead: the Creative Studio, plus
    // producers and pilots, who read Spark and answer their own reviews.
    case 'spark':
      return !!(user?.isadmin || user?.can_admin || user?.can_animator || user?.can_producer || user?.can_pilot);
    case 'pulls':
      // Same gate as the backend's pulls router: an R&D role holder sees the report too.
      return !!(user?.isadmin || user?.can_admin || user?.can_operations || user?.can_manufacturing || user?.can_rnd);
    case 'shopFloor':
      return !!(user?.isadmin || user?.can_admin || user?.can_operations || user?.can_manufacturing);
    case 'chargeControl':
      // Charger fleet (control + asset views). Manufacturing added when charger
      // asset-management moved onto /charge.
      return !!(user?.isadmin || user?.can_admin || user?.can_pilot || user?.can_operations || user?.can_manager || user?.can_manufacturing);
    case 'firmware':
      // Firmware Hub — view/download audience. Mirrors backend canView
      // (pilot | operations | manufacturing | admin). Managing is gated
      // separately to can_manufacturing inside the page.
      return !!(user?.isadmin || user?.can_admin || user?.can_pilot || user?.can_operations || user?.can_manufacturing);
    case 'poa':
      // Plan of Activities — pilots own it, but manufacturing also needs visibility
      // into the weekly schedule. Airspace pieces are gated separately.
      return !!(user?.isadmin || user?.can_admin || user?.can_pilot || user?.can_manufacturing);
    case 'canTravel':
      return !!(user?.isadmin || user?.can_admin || user?.can_operations || user?.can_producer || user?.can_pilot || user?.travel_tier);
    case 'assetsRead':
      // Phase 1a — non-drone asset management. Pilots added so they can view gear
      // and note issues/maintenance (backend flag+maintenance writes admit pilot).
      return !!(user?.isadmin || user?.can_admin || user?.can_operations || user?.can_manufacturing || user?.can_pilot);
    case 'vendorRmaDirect':
      // Vendor RMA (048) is reached from a button on the Returns page, because a
      // tool you use while looking at a screen belongs on that screen. But
      // Returns is gated `procurement` and vendor RMA admits `manufacturing`
      // too — the person holding the failed board is a flash-station tech — so a
      // manufacturing-only user would have had no way in at all.
      //
      // This key is deliberately EXCLUSIVE: it fires only for someone who has
      // manufacturing and NOT procurement. Anyone with procurement gets the
      // button instead, so the menu never carries two entries that mean nearly
      // the same thing to the same person.
      return !!(
        user?.can_manufacturing
        && !(user?.isadmin || user?.can_admin || user?.can_procurement)
      );
    case 'receivingHub':
      // Phase 1b — Receiving Hub. Same gate as backend's RECEIVING_CAPS
      // (operations | manufacturing | procurement | admin).
      return !!(user?.isadmin || user?.can_admin || user?.can_operations || user?.can_manufacturing || user?.can_procurement);
    case 'catalogRead':
      // receivingHub PLUS financial, mirroring the backend's CATALOG_READ_CAPS
      // (parts-catalog-router.js) and the inventory list gate. The bookkeeper
      // prices what the shop receives and reconciles against the catalog, so she
      // reads both lists; she holds no capability that can move stock, and the
      // write routes behind these pages stay on the narrower keys.
      //
      // Deliberately its own key rather than widening receivingHub: that one
      // also gates /receiving, whose route admits only the four. A nav entry
      // whose route bounces you is worse than no entry.
      return !!(
        user?.isadmin || user?.can_admin || user?.can_operations
        || user?.can_manufacturing || user?.can_procurement || user?.can_financial
      );
    case 'workOrders':
      // Work orders (migration 127): catalogRead plus manager, mirroring the
      // backend's read set on /api/work-orders. A production manager approves
      // and closes runs, so must see the inbox even with no other capability.
      return !!(
        user?.isadmin || user?.can_admin || user?.can_operations
        || user?.can_manufacturing || user?.can_procurement || user?.can_financial
        || user?.can_manager
      );
    case 'procurement':
      // Vendor master (ERP expansion chunk 1) — mirrors the backend gate on
      // POST/PATCH /api/procurement/vendors (requireCapability('procurement', 'admin')).
      // Narrower than receivingHub on purpose: operations/manufacturing can
      // receive stock but the backend does not let them write vendor records.
      return !!(user?.isadmin || user?.can_admin || user?.can_procurement);
    case 'familyEditor':
      // Part families: who may rename one and move parts in and out. Mirrors
      // the API's family-editor rule and Parts/familyAccess.ts. Wider than
      // 'procurement' on purpose: a manufacturing manager keeps the parts
      // lists honest too.
      return !!(
        user?.isadmin || user?.can_admin || user?.can_procurement
        || (user?.can_manufacturing && user?.can_manager)
      );
    case 'financial':
      // STRICT — deliberately NOT admin-bypassed. Cost/price visibility is
      // opt-in even for admins; mirrors backend require-financial middleware.
      return !!user?.can_financial;
    default:
      return false;
  }
};

/**
 * Get filtered items for a specific group.
 */
export const getGroupItems = (group, user) =>
  NAV_ITEMS.filter(item => item.group === group && userCanAccess(item, user));

/**
 * Get all visible items for mobile menu (flat list, all groups).
 * Accepts an actions map to resolve action keys to onClick handlers.
 *
 * @param {Object} user - User object
 * @param {Object} actions - Map of action keys to handler functions, e.g. { customize: () => setEditMode(true) }
 * @returns {Array} Items ready for SlideMenu consumption
 */
export const getMobileMenuItems = (user, actions = {}) => {
  return NAV_ITEMS
    .filter(item => userCanAccess(item, user))
    .map(item => {
      const mobileItem = {
        label: item.label.toUpperCase(),
        indent: item.indent,
        external: item.external,
      };

      if (item.action && actions[item.action]) {
        mobileItem.onClick = actions[item.action];
      } else if (item.to) {
        mobileItem.to = item.to;
      }

      // Mobile uses requiresRole/adminOnly for SlideMenu's own filtering,
      // but we've already filtered above, so these aren't needed.
      return mobileItem;
    });
};
